import React, { useMemo } from 'react';
import Section from '../../../layout/Section';
import HorizontalTable, { HorizontalTableRow } from '../../../tables/HorizontalTable';
import { CashflowYear } from '../hooks/useCalculationPageData';
import { formatCurrency } from '../../../../utils/formatters';
import { getGradientColor } from '../../../../utils/colors/colorUtils';
import { colors } from '../../../../styles/colors';

interface CashflowSectionProps {
  data: CashflowYear[];
}

/**
 * Section-Komponente für die Cashflow-Tabelle (CFADS-Methode)
 * Jahre als Spalten, Cashflow-Kategorien als Zeilen
 * 
 * Formeln:
 * - CFADS = Ergebnis vor Steuern + Abschreibungen + Zinsen
 * - DSCR = CFADS / (Tilgung + Zinsen)
 * - Cashflow nach Schuldendienst = CFADS - Zinsen - Tilgung
 */
const CashflowSection: React.FC<CashflowSectionProps> = ({ data }) => {
  // Hilfsfunktion für rot/grün-Farbcodierung bei Cashflow-Zeilen
  const getResultColor = (value: number): string | undefined => {
    if (value < 0) return colors.kpi.gradientTextStart; // rot300 für negative Werte
    if (value > 0) return colors.status.success.main; // grün für positive Werte
    return undefined;
  };

  /**
   * DSCR-Farbcodierung basierend auf Bankbewertungs-Skala
   * 7-stufige Farbskala von dunkelrot bis dunkelgrün
   * Wird für Textfarbe und unteren Balken verwendet
   */
  const getDscrColor = (value: number): string | undefined => {
    if (value === 0) return undefined; // Kein Schuldendienst
    return getGradientColor(value, 1.0, 1.2, colors.kpi.gradientStart, colors.kpi.gradientEnd);
  };

  const getDscrTextColor = (value: number): string | undefined => {
    if (value === 0) return undefined; // Kein Schuldendienst
    return getGradientColor(
      value,
      1.0,
      1.2,
      colors.kpi.gradientTextStart,
      colors.kpi.gradientTextEnd
    );
  };

  // Zeilen-Definitionen mit Keys, Labels, Tooltips und optionalem Highlighting
  // noSum: true bedeutet, dass in der Summen-Spalte "—" angezeigt wird
  // Tooltips für identische Zeilen wie in GuV übernommen
  const rowDefinitions = useMemo(() => [
    {
      key: 'energieerloes',
      label: 'Energieerlös',
      tooltip: '(Ertragswert − Abzüge) × Zuschlagswert × Korrekturfaktor'
    },
    {
      key: 'zinserloes',
      label: 'Zinserlös',
      tooltip: 'Liquiditätsbestand Vorjahr × Guthabenzins'
    },
    {
      key: 'gesamterloes',
      label: 'Gesamterlös',
      highlight: true,
      tooltip: 'Energieerlös + Zinserlös'
    },
    {
      key: 'laufenderAufwand',
      label: 'Lfd. Aufwand',
      tooltip: 'Summe aller jährlichen Betriebskosten (OPEX)'
    },
    {
      key: 'cfads',
      label: 'Cashflow vor Schuldendienst',
      highlight: true,
      tooltip: 'Gesamterlös − Lfd. Aufwand'
    },
    {
      key: 'dscr',
      label: 'DSCR',
      highlight: true,
      colorBar: getDscrColor,
      valueColor: getDscrTextColor,
      isDscr: true,
      noSum: true,
      tooltip: 'Debt Service Coverage Ratio: Cashflow vor Schuldendienst ÷ (Zinsen + Tilgung)'
    },
    {
      key: 'zinsen',
      label: 'Zinsen',
      tooltip: 'Fremdkapitalzinsen aus Tilgungsplan'
    },
    {
      key: 'tilgung',
      label: 'Tilgung',
      tooltip: 'Tilgungsbetrag aus Tilgungsplan'
    },
    {
      key: 'cashflowNachSchuldendienst',
      label: 'Cashflow nach Schuldendienst',
      highlight: true,
      valueColor: getResultColor,
      tooltip: 'Cashflow vor Schuldendienst − Zinsen − Tilgung'
    },
    {
      key: 'liquiditaetsbestand',
      label: 'Liquiditätsbestand',
      highlight: true,
      valueColor: getResultColor,
      noSum: true,
      tooltip: 'Kumulierter Cashflow nach Schuldendienst'
    },
  ], []);

  // Sortiere Daten nach Jahr
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => a.jahr - b.jahr);
  }, [data]);

  // Spaltenüberschriften (Jahre)
  const columnHeaders = useMemo(() =>
    sortedData.map(d => d.jahr),
    [sortedData]
  );

  // Formatiert DSCR als Dezimalzahl mit 2 Nachkommastellen
  const formatDscr = (value: number): string => {
    if (value === 0) return '—'; // Kein Schuldendienst
    return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Konvertiere Zeilen-Definitionen zu HorizontalTableRow-Format
  const rows: HorizontalTableRow[] = useMemo(() =>
    rowDefinitions.map(rowDef => {
      const values = sortedData.map(yearData => {
        return (yearData as unknown as Record<string, number>)[rowDef.key];
      });

      // lastColumnValue: Summe berechnen, außer bei noSum (dann undefined für "—")
      let lastColumnValue: number | undefined;
      if (rowDef.noSum) {
        // Kein Summen-Wert (wird als "—" angezeigt)
        lastColumnValue = undefined;
      } else {
        // Summe aller Werte
        lastColumnValue = values.reduce((sum, v) => sum + (typeof v === 'number' ? v : 0), 0);
      }

      return {
        key: rowDef.key,
        label: rowDef.label,
        values,
        highlightRow: rowDef.highlight,
        valueColor: rowDef.valueColor,
        colorBar: rowDef.colorBar,
        tooltip: rowDef.tooltip,
        lastColumnValue,
      };
    }),
    [rowDefinitions, sortedData]
  );

  // Custom Formatter: DSCR als Dezimalzahl, Rest als Währung
  const customFormatValue = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'string') return value;
    return formatCurrency(value);
  };

  // Custom Cell Renderer für DSCR-Zeile
  const renderCell = (rowKey: string, _columnHeader: string | number, value: string | number | null | undefined): React.ReactNode => {
    if (rowKey === 'dscr') {
      if (value === null || value === undefined) return '—';
      return formatDscr(value as number);
    }
    return customFormatValue(value);
  };

  return (
    <Section title="Cashflow" noPadding>
      <HorizontalTable
        headerLabel="Betriebsjahr"
        columnHeaders={columnHeaders}
        rows={rows}
        lastColumn={{ header: 'Summe' }}
        labelColumnWidth={250}
        dataColumnWidth={120}
        renderCell={renderCell}
      />
    </Section>
  );
};

export default React.memo(CashflowSection);
